import {watchScanRows} from './scan-bars.js'

function dispatchScan(barcode, time) {
  window.dispatchEvent(new CustomEvent('scan:decoded', {detail: {barcode: barcode, time: time}}))
}

// The one map from outcome events to what the device does; the Zebra and the emulator badge both listen through it.
const scanOutcomes = {
  'scan:accepted': 'accepted',
  'scan:refused': 'refused',
  'notify-exception': 'refused',
}
const outcomeListeners = {}

// For outcomes the server cannot send, such as the scanner login.
export function reportScan(accepted) {
  window.dispatchEvent(new CustomEvent(accepted ? 'scan:accepted' : 'scan:refused'))
}

// A refusal that a notice on the screen already states (data-scan-refusal, the hold notice) flashes that notice in place of
// the error toast. Under a sheet or out of view the notice cannot be seen, so the toast shows then.
export function flashShownRefusal(message) {
  const notice = [...document.querySelectorAll('[data-scan-refusal]')]
    .find(el => el.getAttribute('data-scan-refusal') === message && inSight(el))
  if (!notice) return false
  notice.classList.remove('sc-notice-flash')
  // The reflow restarts the flash for a second refusal in a row.
  void notice.offsetWidth
  notice.classList.add('sc-notice-flash')
  return true
}

// Both the top edge and the centre, so a notice half under the header is not in sight.
function inSight(el) {
  const box = el.getBoundingClientRect()
  const x = box.left + box.width / 2
  return [box.top + 1, box.top + box.height / 2].every(y => {
    const top = document.elementFromPoint(x, y)
    return !!top && el.contains(top)
  })
}

// Keyed, so a component that Livewire creates again replaces its listener instead of adding a second one.
function onScanOutcome(name, listener) {
  if (!Object.keys(outcomeListeners).length) listenForScanOutcomes()
  outcomeListeners[name] = listener
}

function listenForScanOutcomes() {
  const tell = outcome => Object.values(outcomeListeners).forEach(listener => listener(outcome))
  window.addEventListener('scan:decoded', () => tell(null))
  Object.entries(scanOutcomes).forEach(([event, outcome]) => window.addEventListener(event, () => tell(outcome)))
  tagScanMessages()
}

// Marks each sent Livewire message that carries a scan (message.carriesScan). Safe to call more than once.
let scanMessagesTagged = false
function tagScanMessages() {
  if (scanMessagesTagged) return
  scanMessagesTagged = true
  onLivewire('message.sent', (message, component) => {
    message.carriesScan = message.updateQueue.some(action => isScan(action, component))
    reportScanBeforeRedirect(component)
  })
}

function watchSheetScans() {
  tagScanMessages()
  watchScanRows(onLivewire, message => !!message.carriesScan)
}

// A decoder receives a scan as @this.set('decoded_at', …); the service screen calls scan().
function isScan(action, component) {
  if (action.type !== 'callMethod') return false
  return (action.payload.method === '$set' && action.payload.params[0] === 'decoded_at')
    || (action.payload.method === 'scan' && component.name === 'scan.service-show')
}

// Livewire drops browser events when a response redirects, so a scan that opens another screen is reported here.
// Only the response to the message that carried the scan counts. Limits: a tap sent in the same message shares the
// scan's feedback, and a scan queued behind a redirecting message is lost with the page.
function reportScanBeforeRedirect(component) {
  if (component.reportsScanBeforeRedirect) return
  component.reportsScanBeforeRedirect = true
  const redirect = component.redirect.bind(component)
  component.redirect = url => {
    if (component.messageInTransit && component.messageInTransit.carriesScan) reportScan(true)
    redirect(url)
  }
}

// The device scanner is off while a screen shows a blocking alert (blockedAlert) or holds a last bad scan (lastBadScan) that
// is not undone yet. The state comes from the components after every response, so a morph, a reload and a new page all end in the same state. Every way out forces the scanner on: a failed
// request, a page restored from the back-forward cache, a tap on an alert button, and a tap whose answer has not come back
// within 5 s. The server still refuses a scan until the alert is closed or the scan undone, so turning on too early is safe; staying off is not.
const answerTimeout = 5000
const alertAnswers = ['closeBlockedAlert', 'discardAndOpen', 'undoLastScan']
let zebraScanner = null
// What the device was last set to; null when a call or the setup failed, so the next sync or release tries again.
let scannerOff = false
let scannerState = false
let answerWatchdog = null
// The alert answer in flight: {component, expired}. After the watchdog, only the answer itself or a new response from
// the alert's own component may turn the scanner off again; another component's response still carries the old state.
let pendingAnswer = null
const scannerStateListeners = {}

export function releaseScanner() {
  turnScanner(false, true)
}

export function answerBlockedAlert(event) {
  const owner = event && event.target && event.target.closest('[wire\\:id]')
  pendingAnswer = {component: owner ? owner.getAttribute('wire:id') : null, expired: false}
  releaseScanner()
  clearTimeout(answerWatchdog)
  answerWatchdog = setTimeout(() => {
    pendingAnswer.expired = true
    releaseScanner()
  }, answerTimeout)
}

function answerResolved() {
  clearTimeout(answerWatchdog)
  pendingAnswer = null
}

function watchScannerState() {
  if (window.scannerStateWatched) return syncScanner()
  window.scannerStateWatched = true
  onLivewire('message.sent', message => {
    message.answersAlert = message.updateQueue.some(action => action.type === 'callMethod' && alertAnswers.includes(action.payload.method))
  })
  onLivewire('message.processed', (message, component) => {
    if (message.answersAlert || (pendingAnswer && pendingAnswer.expired && component && component.id === pendingAnswer.component)) {
      answerResolved()
    }
    syncScanner()
  })
  onLivewire('message.failed', () => {
    answerResolved()
    releaseScanner()
  })
  window.addEventListener('pageshow', event => {
    if (event.persisted) releaseScanner()
    syncScanner()
  })
  window.addEventListener('pagehide', answerResolved)
  window.addEventListener('beforeunload', answerResolved)
  syncScanner()
}

function syncScanner() {
  let off = false
  try {
    off = !!window.Livewire && window.Livewire.all().some(component => !!component.get('blockedAlert') || !!component.get('lastBadScan'))
  } catch (error) {
    console.error(error)
  }
  if (off && pendingAnswer && pendingAnswer.expired) return
  turnScanner(off)
}

function turnScanner(off, force = false) {
  if (off !== scannerState) {
    scannerState = off
    Object.values(scannerStateListeners).forEach(listener => listener(off))
  }
  if (!zebraScanner || (off === scannerOff && !force)) return

  try {
    if (off) zebraScanner.turnOff()
    else zebraScanner.turnOn()
    scannerOff = off
  } catch (error) {
    console.error(error)
    scannerOff = null
    if (off) turnScanner(false)
  }
}

let errorVibration = null

function playOnZebra(outcome) {
  clearInterval(errorVibration)
  if (!outcome || typeof EB === 'undefined' || !EB.Notification) return

  if (outcome === 'accepted') {
    EB.Notification.vibrate(80)
    return
  }

  EB.Notification.beep({frequency: 400, volume: 3, duration: 500})
  const timerId = errorVibration = setInterval(() => EB.Notification.vibrate(100), 200)
  setTimeout(() => clearInterval(timerId), 600)
}

function insideEnterpriseBrowser() {
  return !!window.__rhoNativeApi || /EnterpriseBrowser/i.test(navigator.userAgent)
}

export function zebra() {
  return {
    selectedBarcodeObj: {},
    barcodeObjs: [],
    lastDecoded: 'none',
    init() {
      onScanOutcome('zebra', playOnZebra)

      let enabled = false
      try {
        enabled = this.setupScanner();

      } catch (error) {
        if (insideEnterpriseBrowser()) {
          console.error(error);
          alert('Error setting up Zebra Scanner')
          alert(JSON.stringify(error))
        } else {
          console.warn('Zebra scanner setup failed outside Enterprise Browser - scanner features disabled', error)
        }
      }

      if (this.canSwitch()) {
        zebraScanner = this
        scannerOff = enabled ? false : null
      }
      watchScannerState()
      watchSheetScans()
    },
    // Only the scanner type that setup enables is ever switched. Outside the Enterprise Browser the imager read throws.
    canSwitch() {
      try {
        const scanner = this.selectedBarcodeObj
        return this.isImager() && typeof scanner.enable === 'function' && typeof scanner.disable === 'function'
      } catch (error) {
        return false
      }
    },
    isImager() {
      return !!this.selectedBarcodeObj && this.selectedBarcodeObj.friendlyName == '2D Barcode Imager'
    },
    turnOn() {
      if (this.isImager()) this.enable2Dimager()
    },
    turnOff() {
      this.disableSelected()
    },
    setupScanner() {
      if (typeof EB !== 'undefined' && EB.Barcode !== undefined) {
        this.selectedBarcodeObj = EB.Barcode;
      } else {
        console.warn('Zebra Enterprise Browser SDK not loaded - scanner features disabled')
        return false;
      }
      const enabled = this.enableSelected()
      this.enumerateDevices()
      window.addEventListener('scan:decoded', (event) => {
        this.lastDecoded = event.detail.barcode
      });
      return enabled
    },
    enableSelected() {
      if (this.isImager()) {
        this.enable2Dimager(this.selectedBarcodeObj);
        return true
      }

      if (!insideEnterpriseBrowser()) return false

      alert('Scanner type not setup: ' + this.selectedBarcodeObj.friendlyName ?? '?')
      return false
    },
    enable2Dimager() {
      this.selectedBarcodeObj.enable({
        //Default Options
      }, this.scanEvent);
    },
    scanEvent(data) {
      dispatchScan(data.data, data.time)
    },
    disableSelected() {
      this.selectedBarcodeObj.disable()
    },
    switchTo(barcodeObj) {
      this.disableSelected()
      this.selectedBarcodeObj = barcodeObj
      if (this.enableSelected()) {
        alert(this.selectedBarcodeObj.friendlyName + ' enabled')
      }
    },
    enumerateDevices() {
      if (typeof EB !== 'undefined' && EB.Barcode) {
        EB.Barcode.enumerate(this.enumerateEvent)
      }
    },
    enumerateEvent(barcodeObjects) {
      window.barcodeObjs = barcodeObjects;
    },
    popup(title, msg = "", icon = ":question") {
      if (typeof EB !== 'undefined' && EB.Notification) {
        EB.Notification.showPopup({
          message: msg,
          title: title,
          icon: icon,
          buttons: [{id: 'accept', title: 'Yes'}, {id: 'no', title: 'No'}],
          types: [EB.Notification.TYPE_NOTIFICATION_DIALOG],
        })
      }
    },
    closeApp() {
      if (typeof EB !== 'undefined' && EB.Application) {
        EB.Application.quit();
      }
    },
    // Camera
    showCamera() {
      if (typeof EB !== 'undefined' && EB.Camera) {
        var cameraObj = EB.Camera.enumerate();
        if (cameraObj != "" && cameraObj != null) {
          EB.Camera.takePicture({'outputFormat': 'dataUri'}, this.capturedPicture);
        }
      }
    },
    capturedPicture(data) {
      if ((data.imageUri != undefined) || (data.imageUri != null)) {
        // document.getElementById("captured-image").style.height = "250px";
        // document.getElementById("captured-image").style.width = "250px";
        // document.getElementById("captured-image").src = data.imageUri;
        window.dispatchEvent(
            new CustomEvent('photo:captured', { detail: data.imageUri })
        );
      } else {
        // document.getElementById("captured-image").style.height = "";
        // document.getElementById("captured-image").style.width = "";
        // document.getElementById("captured-image").src = "";
      }
    }
  }
}

function onLivewire(hookName, callback) {
  const hook = () => window.Livewire.hook(hookName, callback)
  window.Livewire ? hook() : document.addEventListener('livewire:load', hook, {once: true})
}

// The emulator cannot beep or vibrate, so it shows what a Zebra would do. The badge lives outside Livewire so a morph
// does not reset it.
function showScanFeedback() {
  const badge = document.createElement('div')
  badge.setAttribute('data-scan-feedback', '')
  badge.style.cssText = 'position: fixed; z-index: 70; top: 0; left: 50%; transform: translateX(32px); display: none;'
    + 'height: 16px; padding: 0 8px; border-radius: 0 0 4px 4px; color: #fff; font-size: 11px; line-height: 16px;'
    + 'font-weight: 700; pointer-events: none; white-space: nowrap;'
  document.body.appendChild(badge)

  let timer = null
  let off = false
  const show = (outcome, text, colour) => {
    clearTimeout(timer)
    badge.dataset.scanFeedback = outcome
    badge.textContent = text
    badge.style.background = colour
    badge.style.display = 'block'
    timer = setTimeout(hide, 2000)
  }
  const showOff = () => {
    badge.dataset.scanFeedback = 'off'
    badge.textContent = 'SCANNER OFF'
    badge.style.background = '#374151'
    badge.style.display = 'block'
  }
  const hide = () => {
    clearTimeout(timer)
    if (off) return showOff()
    badge.dataset.scanFeedback = ''
    badge.style.display = 'none'
  }

  scannerStateListeners.badge = state => {
    off = state
    if (!['accepted', 'refused'].includes(badge.dataset.scanFeedback)) hide()
  }

  onScanOutcome('badge', outcome => {
    if (outcome === 'accepted') show(outcome, 'VIBRATE', '#2F855A')
    else if (outcome === 'refused') show(outcome, 'BEEP / VIBRATE', '#B91C1C')
    else hide()
  })
}

// Item rows mark the barcodes on screen with data-scan-target. Livewire replaces the emulator's Alpine component when it
// morphs the page, so the update hook signals through a window event instead of holding the component.
export function scanEmulator() {
  return {
    open: false,
    first: true,
    awake: false,
    armed: false,
    sleep: null,
    buttons: [],
    init() {
      this.first = !window.scanEmulatorShown
      window.scanEmulatorShown = true
      if (this.first) {
        onLivewire('message.processed', () => window.dispatchEvent(new CustomEvent('scan-emulator:refresh')))
        showScanFeedback()
        watchScannerState()
        watchSheetScans()
      }
    },
    toggle() {
      this.open = !this.open
      if (this.open) this.refresh()
    },
    // Targets on screen come first, then the server presets; a repeated barcode keeps its first place.
    refresh() {
      const visible = el => el.getClientRects().length > 0
      const sheet = [...document.querySelectorAll('[data-scan-sheet]')].filter(visible).pop()
      const onScreen = [...(sheet || document).querySelectorAll('[data-scan-target]')]
        .filter(visible)
        .map(el => ({barcode: el.dataset.scanTarget, label: el.dataset.scanTargetLabel || el.dataset.scanTarget, colour: 'yellow'}))
      const seen = new Set()
      this.buttons = onScreen.concat(this.presets())
        .filter(button => button.barcode && !seen.has(button.barcode) && seen.add(button.barcode))
    },
    presets() {
      try {
        return JSON.parse(this.$el.dataset.scanPresets || '[]')
      } catch (error) {
        console.error(error)
        return []
      }
    },
    short(label) {
      return label.length > 11 ? '…' + label.slice(-10) : label
    },
    // The dim rail lies over the action bar, so a touch on an asleep rail only wakes it; a mouse click scans at once.
    press(event) {
      this.armed = this.awake || event.pointerType === 'mouse'
      this.wake()
    },
    scan(barcode, event) {
      const pressed = event.detail > 0
      if (pressed ? this.armed : this.awake) dispatchScan(barcode, new Date().toISOString())
      this.armed = false
    },
    wake() {
      if (!this.awake) this.refresh()
      this.awake = true
      clearTimeout(this.sleep)
      this.sleep = setTimeout(() => this.awake = false, 3000)
    },
    rest() {
      this.awake = false
      clearTimeout(this.sleep)
    },
  }
}
